import React, { ChangeEvent, useEffect, useState } from 'react'
import { AppDispatch, RootState } from '../../../reducers/store';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast'
import { actionCreateUser, actionFetchUsers } from '../../../actions/users';
import { userValidateConfirmPassword, userValidateEmailAddress, userValidateFirstName, userValidateLastName, userValidateMiddleName, userValidatePassword, userValidateUsername } from '../../../components/validations/users/register';
import { Button, FormField, Loader, PageHeader, SubmitModal } from '../../../components';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { userImg } from '../../../assets';

import './createUser.css'

interface PostDataParams {
  firstName: string;
  middleName: string
  lastName: string
  emailAddress: string
  username: string
  password: string
  confirmPassword: string
}

const CreateUser = () => {
  const pageName                                          = "create user"
  const dispatch                                          = useDispatch<AppDispatch>();
  const [savingInfo, setSavingInfo]                       = useState<boolean>(false);
  const [isButtonDisabled, setIsButtonDisabled]           = useState<boolean>(true);
  const [hideShow, setHideShow]                           = useState<boolean>(false);
  const [onOpen, setOnOpen]                               = useState(false);
  const [firstNameErrors, setFirstNameErrors]             = useState<string[]>([]);
  const [middleNameErrors, setMiddleNameErrors]           = useState<string[]>([]);
  const [lastNameErrors, setLastNameErrors]               = useState<string[]>([]);
  const [emailAddressErrors, setEmailAddressErrors]       = useState<string[]>([]);
  const [usernameErrors, setUsernameErrors]               = useState<string[]>([]);
  const [passwordErrors, setPasswordErrors]               = useState<string[]>([]);
  const [confirmPasswordErrors, setConfirmPasswordErrors] = useState<string[]>([]);
  const [errorMessage, setErrorMessage]                   = useState(null);
  const { getAllUsers, currentPage }                      = useSelector((state: RootState) => state.userList);

  const [ postData, setPostData ]           = useState<PostDataParams>({firstName: '', middleName: '', lastName: '', emailAddress: '', username: '', password: '', confirmPassword: ''})

  const [ isLoadingBtn, setIsLoadingBtn ]   = useState({firstName: false, middleName: false, lastName: false, emailAddress: false, username: false, password: false, confirmPassword: false })

  const [ isValid, setIsValid ]             = useState({firstName: false, middleName: false, lastName: false, emailAddress: false, username: false, password: false, confirmPassword: false })

  const useDebounce = <T,>(value: T, delay: number): T => {
    const [debounced, setDebounced] = useState(value);
  
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebounced(value);
      }, delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
  
    return debounced;
  };

  const debouncedPostData = useDebounce<PostDataParams>(postData, 500)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setIsLoadingBtn((prevState) => ({ ...prevState, [name]: true }));
    setPostData((prevState) => ({ ...prevState, [name]: value }));
  }

  useEffect(() => {
    dispatch(actionFetchUsers({currentPage})); // You can pass the page number as needed
  }, [dispatch, currentPage]);

  useEffect(() => {
    const validateFirstName = () => {
      const { isValid, errors } = userValidateFirstName({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        firstName: isValid,
      }));
      setFirstNameErrors(errors);
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        firstName: false,
      }));
      return errors;
    };

    const validateMiddleName = () => {
      const { isValid, errors } = userValidateMiddleName({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        middleName: isValid,
      }));
      setMiddleNameErrors(errors);
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        middleName: false,
      }));
      return errors;
    };

    const validateLastName = () => {
      const { isValid, errors } = userValidateLastName({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        lastName: isValid,
      }));
      setLastNameErrors(errors);
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        lastName: false,
      }));
      return errors;
    };
  
    const validateEmailAddress = () => {
      const { isValid, errors } = userValidateEmailAddress({ postData: debouncedPostData, getAllUsers });
      setIsValid((prevState) => ({
        ...prevState,
        emailAddress: isValid,
      }));
      setEmailAddressErrors(errors)
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        emailAddress: false,
      }));
      return errors;
    };

    const validateUsername = () => {
      const { isValid, errors } = userValidateUsername({ postData: debouncedPostData, getAllUsers });
      setIsValid((prevState) => ({
        ...prevState,
        username: isValid,
      }));
      setUsernameErrors(errors)
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        username: false,
      }));
      return errors;
    };

    const validatePassword = () => {
      const { isValid, errors } = userValidatePassword({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        password: isValid,
      }));
      setPasswordErrors(errors)
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        password: false,
      }));
      return errors;
    };

    // the confirm password function should connect from the postData since you're comparing two fields
    const validateConfirmPassword = () => {
      const { isValid, errors } = userValidateConfirmPassword({ postData: debouncedPostData });
      setIsValid((prevState) => ({
        ...prevState,
        confirmPassword: isValid,
      }));
      setConfirmPasswordErrors(errors)
      setIsLoadingBtn((prevState) => ({
        ...prevState,
        confirmPassword: false,
      }));
      return errors;
    };
  
    const firstNameErrors        = validateFirstName();
    const middleNameErrors       = validateMiddleName();
    const lastNameErrors         = validateLastName();
    const emailAddressErrors     = validateEmailAddress();
    const usernameErrors         = validateUsername();
    const passwordErrors         = validatePassword();
    const confirmPasswordErrors  = validateConfirmPassword();
    
    setFirstNameErrors(firstNameErrors);
    setMiddleNameErrors(middleNameErrors);
    setLastNameErrors(lastNameErrors);
    setEmailAddressErrors(emailAddressErrors);
    setUsernameErrors(usernameErrors);
    setPasswordErrors(passwordErrors);
    setConfirmPasswordErrors(confirmPasswordErrors);
    
    const hasFormErrors = firstNameErrors.length > 0 || middleNameErrors.length > 0 || lastNameErrors.length > 0 || emailAddressErrors.length > 0 || usernameErrors.length > 0 || passwordErrors.length > 0 || confirmPasswordErrors.length > 0;
    setIsButtonDisabled(hasFormErrors);
  }, [debouncedPostData]);

  const handleModalSubmit = () => {
    setOnOpen(true)
  }

  const handleSubmit = async(e: ChangeEvent<any>) => {
    e.preventDefault();
    handleModalSubmit()
  }

  const cancelPostCreation = () => {
    setOnOpen(false); // Close the modal
    setSavingInfo(false);
  };

  const confirmPostCreation = async() => {
    setSavingInfo(true);
    setIsButtonDisabled(true)
    // console.log(postData)
    const res = await dispatch(actionCreateUser({postData}))
    try{
      if (res.success === true) {
        toast.success("user created successfully");
        setErrorMessage(null);
        setPostData({firstName: '', middleName: '', lastName: '', emailAddress: '', username: '', password: '', confirmPassword: ''})
        setSavingInfo(false)
      } else if (res?.status === 500) {
        setErrorMessage(res.response.data.message);
        setSavingInfo(false)
      }
    }catch(error: any){
      setErrorMessage(res?.status)
      setSavingInfo(false)
    }
    setOnOpen(false); // Close the modal after confirmation
  };

  return (
    <>
      <title>{pageName}</title>
      <PageHeader title="Create User" description="Create User" />

      <div className='account-container'>
        <form className='account-reg-container' onSubmit={handleSubmit} autoComplete="off">
          <div className='account-login-container-title'>
            <h3>Register User</h3>
          </div>

          <div className='account-login-input-btn-pass-container'>
            <FormField inputType type="text" labelName="First Name" name="firstName" value={postData.firstName} handleChange={handleChange} isLoadingBtn={isLoadingBtn.firstName} isValid={isValid.firstName} errors={firstNameErrors || []} />
            
            <FormField inputType type="text" labelName="Middle Name" name="middleName" value={postData.middleName} handleChange={handleChange} isLoadingBtn={isLoadingBtn.middleName} isValid={isValid.middleName} errors={middleNameErrors || []} />

            <FormField inputType type="text" labelName="Last Name" name="lastName" value={postData.lastName} handleChange={handleChange} isLoadingBtn={isLoadingBtn.lastName} isValid={isValid.lastName} errors={lastNameErrors || []} />

            <FormField inputType type="text" labelName="Email Address" name="emailAddress" value={postData.emailAddress} handleChange={handleChange} isLoadingBtn={isLoadingBtn.emailAddress} isValid={isValid.emailAddress} errors={emailAddressErrors || []} />

            <FormField inputType type="text" labelName="Username" name="username" value={postData.username} handleChange={handleChange} isLoadingBtn={isLoadingBtn.username} isValid={isValid.username} errors={usernameErrors || []} />

            <div className='account-login-group-wrapper'>
              <FormField inputType type={hideShow ? 'text' : 'password'} labelName="Password" name="password" value={postData.password} handleChange={handleChange} isLoadingBtn={isLoadingBtn.password} isValid={isValid.password} errors={passwordErrors || []} />
              <div className='account-login-show-hide-pass-container'>
                <span className={ postData.password.length > 0 ? 'show-hide-password unlock' : 'show-hide-password' } onClick={() => setHideShow((prev) => !prev)}>{hideShow ? <VisibilityOff /> : <Visibility />}</span>
              </div>
            </div>
            <FormField inputType type={hideShow ? 'text' : 'password'} labelName="Confirm Password" name="confirmPassword" value={postData.confirmPassword} handleChange={handleChange} isLoadingBtn={isLoadingBtn.confirmPassword} isValid={isValid.confirmPassword} errors={confirmPasswordErrors || []} />

            <SubmitModal onOpen={onOpen} onClose={cancelPostCreation} onConfirm={confirmPostCreation} modalImage={userImg} prompt="Are you sure you want to create post" />
            <Button onClickButton buttonClickWrap={savingInfo ? `button-login-submitted` : `button-login-submit`} onClickName={savingInfo ? <>{<Loader />} Creating...</> : "Sign Up"} isButtonDisabled={isButtonDisabled} buttonClasses={savingInfo ? ['button-disabled'] : (isButtonDisabled ? ['buttonDisabledClass'] : ['buttonEnabledClass'])} disabled={savingInfo} />

            {errorMessage && <p className='error-msg'>{errorMessage}</p>}
          </div>
        </form>
      </div>
    </>
  )
}

export default CreateUser